import { Check, ChevronDown } from "lucide-react";
import { forwardRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * An option displayed in the Combobox list.
 */
export interface ComboboxOption {
  /** The text to display for the option, and what search filters against. */
  label: string;
  /** The unique value associated with the option. */
  value: string;
}

interface ComboboxProps {
  /** Options to be displayed in the dropdown list. */
  options: ComboboxOption[];

  /** The currently selected value. */
  value?: string;

  /** Callback fired when the selected value changes. */
  onValueChange: (value: string) => void;

  /** Placeholder text shown when no value is selected. */
  placeholder?: string;

  /** Placeholder text for the search input. Optional, defaults to "Search...". */
  searchPlaceholder?: string;

  /** Text shown when no options match the search. Optional, defaults to "No results found.". */
  emptyText?: string;

  /** Disables the trigger button. */
  disabled?: boolean;

  /** Additional class names for the trigger button. */
  className?: string;
}

/**
 * A searchable single-select dropdown, built from the existing Popover + Command
 * primitives (same building blocks as MultiSelect) for a single value.
 */
export const Combobox = forwardRef<HTMLButtonElement, ComboboxProps>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = "Select...",
      searchPlaceholder = "Search...",
      emptyText = "No results found.",
      disabled,
      className,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);

    const selectedOption = options.find((option) => option.value === value);

    function handleSelect(optionValue: string) {
      onValueChange(optionValue);
      setOpen(false);
    }

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              "flex h-11 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 font-normal text-sm ring-offset-background hover:bg-background",
              !selectedOption && "text-muted-foreground",
              className
            )}
          >
            <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => handleSelect(option.value)}
                    className="cursor-pointer"
                  >
                    <Check className={cn("h-4 w-4", option.value === value ? "opacity-100" : "opacity-0")} />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

Combobox.displayName = "Combobox";
